<script>
import { Errors } from 'form-backend-validation'
import { trim } from 'lodash'
import moment from 'moment'
import { mapActions } from 'vuex'
import * as FileDownload from 'downloadjs'

export default {
  name: 'SummaryProjectProgressionReport',
  data() {
    return {
      loading: false,
      errors: new Errors(),
      customers: [],
      projects: [],
      resources: [],
      statusOptions: [
        { value: 'Not Started', label: 'Not Started' },
        { value: 'In Progress', label: 'In Progress' },
        { value: 'On Hold', label: 'On Hold' },
        { value: 'Completed', label: 'Completed' },
        { value: 'Cancelled', label: 'Cancelled' }
      ],
      model: {
        customer_id: undefined,
        status: [],
        project_ids: []
      }
    }
  },
  mounted() {
    this.fetchResource()
  },
  methods: {
    moment,
    ...mapActions('report/project', [
      'getCustomer',
      'getProjects',
      'summaryProjectProgression',
      'summaryProjectProgressionExcel'
    ]),
    filter(input, option) {
      return (
        option.componentOptions.children[0].text
          .toLowerCase()
          .indexOf(input.toLowerCase()) >= 0
      )
    },
    async fetchResource() {
      this.loading = true
      try {
        const [customerRes, projectRes] = await Promise.all([
          this.getCustomer(),
          this.getProjects()
        ])

        // Safely extract data arrays from standard or paginated responses
        const rawCustomers = customerRes.data || customerRes || []
        const rawProjects = projectRes.data || projectRes || []

        this.customers = rawCustomers.map(el => ({
          value: el.customer_id || el.id,
          label: el.customer_name || el.name
        }))

        this.projects = rawProjects.map(el => ({
          value: el.project_id || el.id,
          label: el.project_name || el.name
        }))
      } catch (err) {
        console.error('API Fetch Error:', err)
        this.$toasted.error('Failed to load filter options')
      } finally {
        this.loading = false
      }
    },
    validateForm() {
      this.errors.clear()
      if (!this.model.customer_id) {
        this.errors.record({
          customer_id: ['Please select a customer.']
        })
        return false
      }
      return true
    },
    onPreview() {
      if (!this.validateForm()) return

      this.loading = true
      this.summaryProjectProgression(this.model)
        .then(response => {
          this.resources = response.data || response
        })
        .catch(err => {
          this.$toasted.error(err.message || 'Failed to fetch report')
          if (err.errors) {
            this.errors = new Errors(err.errors)
          }
        })
        .finally(() => (this.loading = false))
    },
    exportExcel() {
      if (!this.validateForm()) return

      this.loading = true
      this.errors = new Errors()
      this.summaryProjectProgressionExcel(this.model)
        .then(response => {
          let filename = 'Summary_Project_Progression_Report.xlsx'
          if (response.headers && response.headers['content-disposition']) {
            filename = trim(
              response.headers['content-disposition']
                .substring(
                  response.headers['content-disposition'].indexOf('filename')
                )
                .replace('filename=', ''),
              '"'
            )
          }
          FileDownload(response.data, filename)
        })
        .catch(err => {
          try {
            let error = JSON.parse(
              String.fromCharCode.apply(null, new Uint8Array(err))
            )
            this.$toasted.error(error.message)
            this.errors = new Errors(error.errors)
          } catch (e) {
            this.$toasted.error('Error generating Excel file')
          }
        })
        .finally(() => (this.loading = false))
    }
  }
}
</script>

<template>
  <div>
    <div class="d-flex justify-content-between">
      <h1 class="page-header">
        {{ $t('summaryProjectProgressionReport.pageTitle') }}
      </h1>
      <ts-breadcrumb
        :items="[
          { text: $t('home'), href: '/' },
          {
            text: $t('summaryProjectProgressionReport.pageTitle'),
            active: true
          }
        ]"
      />
    </div>
    <ts-loading-banner :loading="loading">
      <div class="row">
        <!-- Filter Controls Sidebar -->
        <div class="col-md-3">
          <div class="form-group ts-h-full">
            <ts-card class="ts-h-full">
              <ts-card-wrapper class="ts-h-full">
                <form @submit.prevent="onPreview">
                  <fieldset>
                    <legend>{{ $t('performanceReport.criteria') }}</legend>

                    <!-- Filter 1: Customers -->
                    <div class="row">
                      <div class="col-md-12">
                        <div class="form-group">
                          <label>
                            {{
                              $t(
                                'summaryProjectProgressionReport.selectCustomer'
                              )
                            }}
                            <span class="text-danger">*</span>
                          </label>
                          <a-select
                            v-model="model.customer_id"
                            :options="customers"
                            showSearch
                            :filterOption="filter"
                            :placeholder="
                              $t(
                                'summaryProjectProgressionReport.selectCustomerPlaceholder'
                              )
                            "
                            class="ts-w-full"
                            :allowClear="true"
                            @change="errors.clear('customer_id')"
                          />
                          <div
                            class="validate"
                            v-if="errors.has('customer_id')"
                          >
                            {{ errors.first('customer_id') }}
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- Filter 2: Project Status (Multi Select) -->
                    <div class="row">
                      <div class="col-md-12">
                        <div class="form-group">
                          <label>{{
                            $t('summaryProjectProgressionReport.selectStatus')
                          }}</label>
                          <a-select
                            v-model="model.status"
                            :options="statusOptions"
                            showSearch
                            :filterOption="filter"
                            :placeholder="$t('all')"
                            mode="multiple"
                            class="ts-w-full"
                            :max-tag-count="1"
                          />
                        </div>
                      </div>
                    </div>

                    <!-- Filter 3: Projects (Multi Select) -->
                    <div class="row">
                      <div class="col-md-12">
                        <div class="form-group">
                          <label>{{
                            $t('summaryProjectProgressionReport.selectProject')
                          }}</label>
                          <a-select
                            v-model="model.project_ids"
                            :options="projects"
                            showSearch
                            :filterOption="filter"
                            :placeholder="$t('all')"
                            mode="multiple"
                            class="ts-w-full"
                            :max-tag-count="1"
                            :allowClear="true"
                            @change="errors.clear('project_ids')"
                          />
                          <div
                            class="validate"
                            v-if="errors.has('project_ids')"
                          >
                            {{ errors.first('project_ids') }}
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- Action Buttons -->
                    <div class="row ts-mt-4">
                      <div
                        class="col-md-12 ts-flex ts-justify-end ts-space-x-2"
                      >
                        <ts-button @click="onPreview" color="primary">
                          {{ $t('preview') }}
                        </ts-button>
                        <ts-button @click="exportExcel" color="info">
                          {{ $t('excel') }}
                        </ts-button>
                      </div>
                    </div>
                  </fieldset>
                </form>
              </ts-card-wrapper>
            </ts-card>
          </div>
        </div>

        <!-- Report Data Preview Grid -->
        <div class="col-md-9">
          <div class="form-group ts-h-full">
            <ts-card class="ts-h-full">
              <ts-card-wrapper class="ts-h-full">
                <h1 class="ts-text-base">{{ $t('preview') }}</h1>
                <div class="ts-w-full ts-h-full">
                  <div
                    v-if="resources.length === 0"
                    class="ts-w-full ts-h-full ts-flex ts-justify-center ts-items-center"
                    style="min-height: 250px;"
                  >
                    <div class="ts-text-center">
                      <i class="fas fa-folder-open fa-3x ts-text-gray-400"></i>
                      <div>{{ $t('noData') }}</div>
                    </div>
                  </div>

                  <div
                    v-else
                    class="ts-mt-4 ts-overflow-auto"
                    style="height: 500px;"
                  >
                    <table
                      class="ts-w-full ts-text-sm border"
                      style="min-width: 1400px;"
                    >
                      <thead>
                        <tr class="ts-bg-green-600 ts-text-white">
                          <th
                            class="ts-py-2 ts-px-2 ts-sticky ts-top-0 ts-bg-green-600 border ts-whitespace-nowrap"
                            style="width: 50px;"
                          >
                            {{ $t('summaryProjectProgressionReport.no') }}
                          </th>
                          <th
                            class="ts-py-2 ts-px-2 ts-sticky ts-top-0 ts-bg-green-600 border ts-whitespace-nowrap"
                            style="min-width: 220px;"
                          >
                            {{
                              $t('summaryProjectProgressionReport.projectName')
                            }}
                          </th>
                          <th
                            class="ts-py-2 ts-px-2 ts-sticky ts-top-0 ts-bg-green-600 border ts-whitespace-nowrap"
                            style="min-width: 300px;"
                          >
                            {{
                              $t('summaryProjectProgressionReport.serviceDes')
                            }}
                          </th>
                          <th
                            class="ts-py-2 ts-px-2 ts-sticky ts-top-0 ts-bg-green-600 border ts-whitespace-nowrap"
                            style="min-width: 140px;"
                          >
                            {{ $t('summaryProjectProgressionReport.team') }}
                          </th>
                          <th
                            class="ts-py-2 ts-px-2 ts-sticky ts-top-0 ts-bg-green-600 border ts-text-center ts-whitespace-nowrap"
                            style="min-width: 90px;"
                          >
                            {{ $t('summaryProjectProgressionReport.planQty') }}
                          </th>
                          <th
                            class="ts-py-2 ts-px-2 ts-sticky ts-top-0 ts-bg-green-600 border ts-text-center ts-whitespace-nowrap"
                            style="min-width: 110px;"
                          >
                            {{
                              $t('summaryProjectProgressionReport.startDate')
                            }}
                          </th>
                          <th
                            class="ts-py-2 ts-px-2 ts-sticky ts-top-0 ts-bg-green-600 border ts-text-center ts-whitespace-nowrap"
                            style="min-width: 170px;"
                          >
                            {{
                              $t(
                                'summaryProjectProgressionReport.actualQtyCompleted'
                              )
                            }}
                          </th>
                          <th
                            class="ts-py-2 ts-px-2 ts-sticky ts-top-0 ts-bg-green-600 border ts-text-center ts-whitespace-nowrap"
                            style="min-width: 130px;"
                          >
                            {{
                              $t(
                                'summaryProjectProgressionReport.completedDate'
                              )
                            }}
                          </th>
                          <th
                            class="ts-py-2 ts-px-2 ts-sticky ts-top-0 ts-bg-green-600 border ts-text-center ts-whitespace-nowrap"
                            style="min-width: 120px;"
                          >
                            {{ $t('summaryProjectProgressionReport.status') }}
                          </th>
                          <th
                            class="ts-py-2 ts-px-2 ts-sticky ts-top-0 ts-bg-green-600 border ts-text-center ts-whitespace-nowrap"
                            style="min-width: 130px;"
                          >
                            {{
                              $t(
                                'summaryProjectProgressionReport.progressionPercent'
                              )
                            }}
                          </th>
                          <th
                            class="ts-py-2 ts-px-2 ts-sticky ts-top-0 ts-bg-green-600 border ts-text-center ts-whitespace-nowrap"
                            style="min-width: 130px;"
                          >
                            {{
                              $t('summaryProjectProgressionReport.trcDeadline')
                            }}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr
                          v-for="(item, index) in resources"
                          :key="index"
                          class="ts-border-b"
                        >
                          <td
                            class="ts-px-2 ts-py-1 border ts-text-center ts-whitespace-nowrap"
                          >
                            {{ index + 1 }}
                          </td>
                          <td
                            class="ts-px-2 ts-py-1 border ts-whitespace-nowrap"
                          >
                            {{ item.project_name }}
                          </td>
                          <td class="ts-px-2 ts-py-1 border">
                            {{ item.service_description }}
                          </td>
                          <td
                            class="ts-px-2 ts-py-1 border ts-whitespace-nowrap"
                          >
                            {{ item.team }}
                          </td>
                          <td
                            class="ts-px-2 ts-py-1 border ts-text-center ts-whitespace-nowrap"
                          >
                            {{ item.plan_qty }}
                          </td>
                          <td
                            class="ts-px-2 ts-py-1 border ts-text-center ts-whitespace-nowrap"
                          >
                            {{ item.start_date }}
                          </td>
                          <td
                            class="ts-px-2 ts-py-1 border ts-text-center ts-whitespace-nowrap"
                          >
                            {{ item.actual_qty_completed }}
                          </td>
                          <td
                            class="ts-px-2 ts-py-1 border ts-text-center ts-whitespace-nowrap"
                          >
                            {{ item.completed_date }}
                          </td>
                          <td
                            class="ts-px-2 ts-py-1 border ts-text-center ts-whitespace-nowrap"
                          >
                            {{ item.status }}
                          </td>
                          <td
                            class="ts-px-2 ts-py-1 border ts-text-center font-bold ts-whitespace-nowrap"
                          >
                            {{ item.progression_percent }}%
                          </td>
                          <td
                            class="ts-px-2 ts-py-1 border ts-text-center ts-whitespace-nowrap"
                          >
                            {{ item.trc_deadline }}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </ts-card-wrapper>
            </ts-card>
          </div>
        </div>
      </div>
    </ts-loading-banner>
  </div>
</template>
